import { create } from 'zustand';

import {
  type ConfirmationConfig,
  type ReminderPollConfig,
  type ReminderResponse,
  type ReminderRichMessage,
  type ReminderStatus,
  type ReminderType,
} from '@irl/types';
import {
  createDefaultRecurrenceRule,
  fromLegacySchedule,
  type RecurrenceRule,
} from '@irl/validation';

import { buildQuickRecurrenceRule, type QuickCreateConfig } from '@/lib/quick-create-reminder';
import { DEFAULT_TIMEZONE } from '@/lib/timezone-options';

function resolveStartDateLocal(reminder: ReminderResponse): string {
  if (reminder.startDateLocal) {
    return reminder.startDateLocal;
  }

  // TODO: remove fallback once API always returns startDateLocal on reminder responses
  console.warn(
    '[create-reminder-wizard] startDateLocal missing; deriving from startDate + timezone',
    { reminderId: reminder.id, timezone: reminder.timezone },
  );
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: reminder.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(reminder.startDate));
}

/** Older divider blocks may omit content. Keep every block and media reference. */
function editableRichMessage(message: ReminderRichMessage | undefined): ReminderRichMessage {
  return {
    blocks: (message?.blocks ?? []).map((block) => ({
      ...structuredClone(block),
      content: structuredClone(block.content ?? { type: 'plain', text: '' }),
    })),
  };
}

interface WizardState {
  substitutions: import('@irl/types').ReminderSubstitutions | null;
  counterOrdinal: number;
  poll: ReminderPollConfig | null;
  confirmation: ConfirmationConfig;
  editingReminderId: string | null;
  hydratedFromId: string | null;
  createSessionActive: boolean;
  title: string;
  description: string;
  reminderType: ReminderType;
  saveAsDraft: boolean;
  copiedFrom: string | null;
  messageTemplate: string;
  useRichFormat: boolean;
  richMessage: ReminderRichMessage;
  messageType: 'text' | 'photo' | 'document';
  sendMode: 'now' | 'schedule';
  recurrenceRule: RecurrenceRule;
  timezoneTouched: boolean;
  chatId: string;
  targetChatIds: string[];
  targetUserIds: string[];
  status: ReminderStatus;
  setField: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void;
  setRecurrenceRule: (rule: RecurrenceRule) => void;
  setTimezone: (timezone: string, manual?: boolean) => void;
  beginCreate: (quick?: QuickCreateConfig) => void;
  endCreateSession: () => void;
  copyFromReminder: (reminder: ReminderResponse) => void;
  loadFromReminder: (reminder: ReminderResponse) => void;
  reset: () => void;
}

function createInitialRecurrenceRule(): RecurrenceRule {
  return createDefaultRecurrenceRule({
    type: 'weekly',
    timezone: DEFAULT_TIMEZONE,
    weekdays: [1, 2, 3, 4, 5],
  });
}

function buildCreateState(quick?: QuickCreateConfig): Omit<typeof initialState, 'sendMode'> & {
  sendMode: 'now' | 'schedule';
  createSessionActive: true;
} {
  const recurrenceRule = quick?.recurrenceType
    ? buildQuickRecurrenceRule(quick.recurrenceType)
    : createInitialRecurrenceRule();

  return {
    ...initialState,
    recurrenceRule,
    sendMode: quick?.sendMode ?? 'schedule',
    createSessionActive: true,
  };
}

const initialState = {
  substitutions: null as import('@irl/types').ReminderSubstitutions | null,
  counterOrdinal: 0,
  poll: null as ReminderPollConfig | null,
  confirmation: {
    repeatEnabled: false,
    audience: 'all',
    userIds: [],
    intervalMinutes: 60,
    deadlineHours: 24,
  } as ConfirmationConfig,
  editingReminderId: null as string | null,
  hydratedFromId: null as string | null,
  createSessionActive: false,
  title: '',
  description: '',
  reminderType: 'custom' as ReminderType,
  saveAsDraft: false,
  copiedFrom: null as string | null,
  messageTemplate: '',
  useRichFormat: true,
  richMessage: { blocks: [] },
  messageType: 'text' as const,
  sendMode: 'schedule' as const,
  recurrenceRule: createInitialRecurrenceRule(),
  timezoneTouched: false,
  chatId: '',
  targetChatIds: [] as string[],
  targetUserIds: [] as string[],
  status: 'draft' as ReminderStatus,
};

export const useCreateReminderWizard = create<WizardState>((set) => ({
  ...initialState,
  setField: (key, value) => {
    set({ [key]: value });
  },
  setRecurrenceRule: (rule) => {
    set({ recurrenceRule: rule });
  },
  setTimezone: (timezone, manual = false) => {
    set((state) => ({
      recurrenceRule: { ...state.recurrenceRule, timezone },
      timezoneTouched: manual ? true : state.timezoneTouched,
    }));
  },
  beginCreate: (quick) => {
    set(buildCreateState(quick));
  },
  endCreateSession: () => {
    set({ createSessionActive: false });
  },
  copyFromReminder: (reminder) => {
    set({
      ...buildCreateState({ recurrenceType: 'once' }),
      copiedFrom: reminder.title,
      title: reminder.title,
      description: reminder.description ?? '',
      reminderType: reminder.type,
      confirmation: reminder.confirmation
        ? structuredClone(reminder.confirmation)
        : structuredClone(initialState.confirmation),
      substitutions: reminder.substitutions ? structuredClone(reminder.substitutions) : null,
      poll: reminder.poll ? structuredClone(reminder.poll) : null,
      messageTemplate: reminder.messageTemplate,
      useRichFormat: reminder.useRichFormat ?? false,
      richMessage: editableRichMessage(reminder.richMessage),
      chatId: reminder.chatId,
      targetChatIds: reminder.targetChatIds.length
        ? [...reminder.targetChatIds]
        : [reminder.chatId],
      targetUserIds: [...reminder.targetUserIds],
      recurrenceRule: {
        ...createDefaultRecurrenceRule({ type: 'once', timezone: reminder.timezone }),
        startDate: null,
        time: null,
      },
    });
  },
  loadFromReminder: (reminder) => {
    const targetChatIds =
      reminder.targetChatIds.length > 0 ? reminder.targetChatIds : [reminder.chatId];

    set({
      counterOrdinal:
        reminder.substitutions && reminder.nextCounter !== undefined
          ? (reminder.nextCounter - reminder.substitutions.counterStart) /
            reminder.substitutions.counterStep
          : 0,
      editingReminderId: reminder.id,
      hydratedFromId: reminder.id,
      title: reminder.title,
      description: reminder.description ?? '',
      reminderType: reminder.type,
      confirmation: reminder.confirmation
        ? structuredClone(reminder.confirmation)
        : structuredClone(initialState.confirmation),
      saveAsDraft: false,
      copiedFrom: null,
      substitutions: reminder.substitutions ? structuredClone(reminder.substitutions) : null,
      poll: reminder.poll ? structuredClone(reminder.poll) : null,
      messageTemplate: reminder.messageTemplate,
      useRichFormat: reminder.useRichFormat ?? false,
      richMessage: editableRichMessage(reminder.richMessage),
      messageType: 'text',
      sendMode: 'schedule',
      recurrenceRule: {
        ...fromLegacySchedule({
          scheduleType: reminder.scheduleType,
          scheduleConfig: reminder.scheduleConfig,
          startDate: new Date(reminder.startDate),
          ...(reminder.endDate ? { endDate: new Date(reminder.endDate) } : {}),
          timezone: reminder.timezone,
        }),
        startDate: resolveStartDateLocal(reminder),
      },
      timezoneTouched: true,
      chatId: reminder.chatId,
      targetChatIds,
      targetUserIds: reminder.targetUserIds,
      status: reminder.status,
      createSessionActive: true,
    });
  },
  reset: () => {
    set({
      ...initialState,
      recurrenceRule: createInitialRecurrenceRule(),
      createSessionActive: false,
    });
  },
}));
